/**
 * demoNarrationModel.ts
 *
 * The demo's caption track as the app reads it (card D16, plan §4.11). The hub
 * (D14) owns *what is said and when*: one `demo:narration` per cue change, with
 * the live HUD line already composed and one rendered Piper clip per language.
 * This module owns everything the app must decide the same way every time, and
 * nothing else — no Vue, no DOM, no socket:
 *
 *  - **The caption's own words** (`captionFor`): the `DEMO // DAY n · HH:MM`
 *    header, the body in the reader's language, the data line beneath it.
 *  - **How long a caption stays up** (`holdMs`): long enough to read, short
 *    enough that the next beat is not late. 45 ms per character, floored at
 *    4.5 s (a short line still needs a moment), capped at 12 s (a long one must
 *    not outlive its own moment by minutes at 288×).
 *  - **The queue** (one visible, one pending, the oldest dropped on overflow):
 *    a caption that arrives while another is still being read is QUEUED, never
 *    painted over it — the whole point of a subtitle track is that the words and
 *    the moment line up. At 900× two cues can land inside one hold; dropping the
 *    oldest is what keeps the screen from falling minutes behind the house.
 *  - **Whether a cue is SPOKEN** (`shouldSpeak`): a spoken line at 900× is
 *    gibberish chasing a clock nobody is watching, so speech rides the same
 *    speed gate the rest of the demo does (≤ 60×), and it never plays while the
 *    clock is paused or for a cue a SEEK landed on (the operator jumped; the
 *    show did not speak).
 *  - **The clip's URL** (`narrationAudioPath` + `narrationAudioUrl`): the hub
 *    hands out hub-relative paths (`/uploads/demo-scripts/…`), which a page the
 *    hub serves can use as-is — and a page it does NOT serve (the desktop app on
 *    `file://`, the phone on its own scheme) cannot, so the path is joined
 *    against the hub's own base exactly as the camera stream URLs are
 *    (`services/cameraStreamUrls.ts`, rule 21 of the otto-ui skill).
 *
 * The reader (`readDemoNarrationCue`) is TOTAL, on purpose: it accepts both the
 * socket's payload and the timeline's `narration[]` row (which has no `simMs`
 * and no `dataSource`), never throws, and answers `null` for anything it cannot
 * name — a caption board that throws inside a computed is a blank screen.
 */
import { isPrivateOrLocalHostname } from '../config/runtime';

/** The two languages a cue ships in — exactly the two `text`/`audio` keys. */
export type DemoNarrationLanguage = 'en' | 'sl';

/** The languages, in the order the script authors them. */
export const NARRATION_LANGUAGES: DemoNarrationLanguage[] = ['en', 'sl'];

/** One rendered clip per language (`null` until the hub's render exists). */
export interface DemoNarrationAudioUrls {
  en: string | null;
  sl: string | null;
}

/**
 * One caption cue, as the app reads it.
 *
 * Both sources collapse into this: the socket's `demo:narration` payload (D14)
 * and a `GET /api/demo/timeline` day's `narration[]` row (D13). The fields the
 * timeline does not carry are `null` here rather than invented.
 */
export interface DemoNarrationCue {
  /** The cue id (`wake`, `n3` …) — stable inside one day. */
  id: string;
  /** The scripted day it belongs to (`''` when the source omitted it). */
  dayId: string;
  /** `HH:MM` in simulated time (the header's tail). */
  at: string;
  /** The instant it fired for (epoch ms, simulated) — `null` off the timeline. */
  simMs: number | null;
  /** …and as an ISO string, the clock chip's own spelling. */
  simIso: string | null;
  /** The authored line, both languages. */
  text: { en: string; sl: string };
  /** The HUD line (the author's override, else composed from the world). */
  data: string;
  /** Where `data` came from, when the sender said (`cue` = authored override). */
  dataSource: 'cue' | 'world' | null;
  /** The rendered clips, per language. */
  audio: DemoNarrationAudioUrls;
  /** A seek landed here: show the caption, play no audio. */
  seeking: boolean;
  /** The cut this cue belongs to, when the author tied it to one. */
  cutId: string | null;
}

/** A caption, ready to paint: the header, the body, the data line beneath. */
export interface NarrationCaption {
  /** `DEMO // DAY 1 · 06:45` — brand, day and sim time in one line. */
  header: string;
  /** The line itself, in the reader's language. */
  body: string;
  /** The live HUD line (`OCC 2 · KITCHEN · 19.4 °C · LUX 21350`), or `''`. */
  data: string;
}

/** How the header words the day (`DAY`/`DAN` — the caller's `tt()`). */
export interface NarrationCaptionOptions {
  /** 1-based day number; defaults to 1 (the first day of a script). */
  dayNumber?: number;
  /** The word for `DAY` in the reader's language; defaults to `DAY`. */
  dayWord?: string;
}

/** How long one typed character takes (the skin's own figure, 26 ms). */
export const NARRATION_TYPE_MS = 26;

/** A caption never flashes by: a short line still holds this long. */
export const NARRATION_HOLD_MIN_MS = 4_500;

/** Reading speed, per character of the body. */
export const NARRATION_HOLD_PER_CHAR_MS = 45;

/** …and a long line never holds the screen longer than this. */
export const NARRATION_HOLD_MAX_MS = 12_000;

/** Above this rate the day is a blur: captions still flow, nothing is spoken. */
export const NARRATION_SPEAK_MAX_RATE = 60;

/** A body is clamped to this many lines by the overlay (skin B's own rule). */
export const NARRATION_MAX_LINES = 3;

/** The queue's two slots — the one being read, and the one waiting. */
export interface NarrationQueue {
  visible: DemoNarrationCue | null;
  pending: DemoNarrationCue | null;
}

/** Nothing said yet. */
export const EMPTY_NARRATION_QUEUE: NarrationQueue = { visible: null, pending: null };

/** The language a cue is read and spoken in; anything but `sl` reads English. */
export function narrationLanguage(code: string | null | undefined): DemoNarrationLanguage {
  return String(code ?? '').toLowerCase().startsWith('sl') ? 'sl' : 'en';
}

/** The cue's identity across a re-send — the hub's own key (`<dayId>:<id>`). */
export function narrationCueKey(cue: DemoNarrationCue): string {
  return `${cue.dayId}:${cue.id}`;
}

/** A trimmed string, or `''` for anything that is not one. */
function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** A nullable string: `''` reads as `null` (the wire's own "nothing"). */
function optionalText(value: unknown): string | null {
  const trimmed = text(value);
  return trimmed === '' ? null : trimmed;
}

/** The `audio` pair off either payload shape, always an object. */
function readAudio(value: unknown): DemoNarrationAudioUrls {
  const bag = (value ?? {}) as Record<string, unknown>;
  return { en: optionalText(bag.en), sl: optionalText(bag.sl) };
}

/**
 * Read one cue from the socket's payload or the timeline's row.
 *
 * Total by contract: a partial payload answers `null` (nothing to paint), never
 * a half-filled cue, and never a throw. A cue whose two languages are both
 * empty is nothing to say — `null` again.
 */
export function readDemoNarrationCue(payload: unknown): DemoNarrationCue | null {
  if (!payload || typeof payload !== 'object') return null;
  const bag = payload as Record<string, unknown>;

  const id = text(bag.id);
  if (!id) return null;

  const authored = (bag.text ?? {}) as Record<string, unknown>;
  const en = text(authored.en);
  const sl = text(authored.sl);
  if (!en && !sl) return null;

  const source = bag.dataSource;
  const simMs = typeof bag.simMs === 'number' && Number.isFinite(bag.simMs) ? bag.simMs : null;

  return {
    id,
    dayId: text(bag.dayId),
    at: text(bag.at),
    simMs,
    simIso: optionalText(bag.simIso),
    // One language is enough to show a line, and the other falls back at paint
    // time (`captionFor`) — a half-translated script must not blank the board.
    text: { en: en || sl, sl: sl || en },
    data: text(bag.data),
    dataSource: source === 'cue' || source === 'world' ? source : null,
    audio: readAudio(bag.audio),
    seeking: bag.seeking === true,
    cutId: optionalText(bag.cutId),
  };
}

/**
 * The caption's three lines: the header, the body in `language`, the data line.
 *
 * `captionFor(cue, language)` is the two-argument shape every caller uses; the
 * day number and the word for `DAY` are the optional third argument because
 * they belong to the clock (1-based, localised) rather than to the cue.
 */
export function captionFor(
  cue: DemoNarrationCue,
  language: DemoNarrationLanguage,
  options: NarrationCaptionOptions = {},
): NarrationCaption {
  const dayNumber = Number.isFinite(options.dayNumber) ? Math.max(1, Math.round(options.dayNumber as number)) : 1;
  const dayWord = (options.dayWord ?? '').trim() || 'DAY';
  const at = cue.at.trim();

  return {
    header: at ? `DEMO // ${dayWord} ${dayNumber} · ${at}` : `DEMO // ${dayWord} ${dayNumber}`,
    // The authored line in the reader's language; a script that left one empty
    // still has the other, and showing the other beats showing nothing.
    body: cue.text[language] || cue.text[language === 'sl' ? 'en' : 'sl'] || '',
    data: cue.data,
  };
}

/**
 * How long a caption holds: 45 ms per character, floored at 4.5 s and capped at
 * 12 s. Counts CODE POINTS, so an accented or emoji-adjacent character is one
 * character and a Slovenian `č` costs what an English `c` costs.
 */
export function holdMs(body: string): number {
  const chars = Array.from(String(body ?? '')).length;
  return Math.min(NARRATION_HOLD_MAX_MS, Math.max(NARRATION_HOLD_MIN_MS, chars * NARRATION_HOLD_PER_CHAR_MS));
}

/**
 * Put one cue into the queue (one visible, one pending, the oldest dropped).
 *
 * Returns the SAME object it was given when the cue is already in the queue —
 * the hub re-sends a cue after a reconnect, and a duplicate must not restart the
 * hold timer or speak twice. The visible cue is never replaced while it holds:
 * a newly arriving caption waits, and only a THIRD cue (the overflow case)
 * retires the one being read, so the board shows the newest two beats rather
 * than lagging a minute behind a fast day.
 */
export function enqueueCue(queue: NarrationQueue, cue: DemoNarrationCue): NarrationQueue {
  const key = narrationCueKey(cue);
  if (queue.visible && narrationCueKey(queue.visible) === key) return queue;
  if (queue.pending && narrationCueKey(queue.pending) === key) return queue;

  if (!queue.visible) return { visible: cue, pending: null };
  if (!queue.pending) return { visible: queue.visible, pending: cue };

  // Overflow: the oldest is what was on screen; the pending line promotes and
  // the new cue takes the waiting slot.
  return { visible: queue.pending, pending: cue };
}

/** The hold expired: the waiting cue (if any) takes the screen, else nothing. */
export function advanceQueue(queue: NarrationQueue): NarrationQueue {
  if (!queue.pending) return EMPTY_NARRATION_QUEUE;
  return { visible: queue.pending, pending: null };
}

/** What the speech gate reads. */
export interface NarrationSpeakInput {
  /** Narration switched on (the operator's own preference). */
  enabled: boolean;
  /** The demo clock is advancing. */
  playing: boolean;
  /** The day is running at this rate (sim-ms per real-ms). */
  rate: number;
  /** This cue is where a seek landed. */
  seeking: boolean;
}

/**
 * Is this cue spoken? A caption at 900× is read, never narrated — the voice is
 * for a day a person can follow (≤ 60×), while the clock plays, and only for
 * the beats the show actually reached (a seek shows the line and stays silent).
 */
export function shouldSpeak(input: NarrationSpeakInput): boolean {
  if (!input.enabled) return false;
  if (!input.playing) return false;
  if (input.seeking) return false;
  if (!Number.isFinite(input.rate) || input.rate <= 0) return false;
  return input.rate <= NARRATION_SPEAK_MAX_RATE;
}

/** The clip's hub-relative path for a language, or `null` when it is silent. */
export function narrationAudioPath(cue: DemoNarrationCue, language: DemoNarrationLanguage): string | null {
  return cue.audio[language] ?? null;
}

/**
 * Resolve a clip path against the hub's own base.
 *
 * The rule is `services/cameraStreamUrls.ts`'s, applied to a second payload that
 * hands out a same-origin path: a page the hub SERVES keeps the path (mixed
 * content and the cloud channel both want it untouched), a direct hub base
 * (a LAN address on the phone, the desktop app's real hub address) gets the
 * path joined onto it, and an absolute URL is returned as it came.
 *
 * The caller hands in a base that a MEDIA ELEMENT can dial — `toDirectHubApiUrl`
 * (`config/runtime`) converts the desktop app's internal `otto-local://` scheme
 * to `http(s)://`, because that scheme is the renderer's fetch proxy and an
 * `<audio>` element cannot use it.
 */
export function narrationAudioUrl(path: string | null, apiUrl = ''): string | null {
  if (!path) return null;
  if (!path.startsWith('/')) return path;

  const base = (apiUrl || '').trim();
  // A relative/empty base is the hub-served page itself; a web (cloud) origin
  // is carried by the WebRTC channel, where an absolute URL would be wrong.
  if (!base || base.startsWith('/') || !isDirectHubBase(base)) return path;

  try {
    // An absolute path replaces the base's own path, so `http://hub/api` joins
    // correctly (`/uploads/…` is served from the hub's root).
    return new URL(path, base).toString();
  } catch {
    return path;
  }
}

/** Is this base a hub the app dials directly (rather than a web origin)? */
function isDirectHubBase(base: string): boolean {
  try {
    return isPrivateOrLocalHostname(new URL(base, 'http://localhost').hostname);
  } catch {
    return false;
  }
}
